import { createContext, useContext, useEffect, useState, useMemo } from 'react';

/**
 * Theme: 'system' | 'light' | 'dark'.
 *
 * 'system' follows prefers-color-scheme and reacts to OS changes live.
 * Selection persists to localStorage. Applied via a class on
 * document.documentElement ('theme-light' or 'theme-dark') so CSS uses
 * a single set of variables overridden by that class.
 *
 * On first load, the choice is read SYNCHRONOUSLY before React mounts
 * to avoid a flash of the wrong theme — see frontend/index.html's
 * inline script.
 */
const ThemeContext = createContext(null);

const STORAGE_KEY = 'markaii.theme';
const THEMES = ['system', 'light', 'dark'];

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (THEMES.includes(raw)) return raw;
  } catch { /* ignore */ }
  return 'system';
}

function resolveEffective(theme) {
  if (theme !== 'system') return theme;
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  }
  return 'dark';
}

function applyClass(effective) {
  const root = document.documentElement;
  root.classList.toggle('theme-light', effective === 'light');
  root.classList.toggle('theme-dark', effective === 'dark');
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readStored());
  const [effective, setEffective] = useState(() => resolveEffective(readStored()));

  // Re-evaluate effective theme when `theme` changes
  useEffect(() => {
    const next = resolveEffective(theme);
    setEffective(next);
    applyClass(next);
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
  }, [theme]);

  // Live-react to OS theme changes when `theme === 'system'`
  useEffect(() => {
    if (theme !== 'system' || typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => {
      const next = mq.matches ? 'light' : 'dark';
      setEffective(next);
      applyClass(next);
    };
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, [theme]);

  const value = useMemo(() => ({
    theme,
    effective,
    setTheme: (next) => {
      if (!THEMES.includes(next)) return;
      setThemeState(next);
    },
  }), [theme, effective]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be inside <ThemeProvider>');
  return ctx;
}
